import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/UIElements/Button";
import { Icon } from "../../ui/UIElements/Icon";
import { Label } from "../../ui/UIElements/Label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../ui/UIElements/Popover";
import { useUiLocale } from "../../../hooks/useUiLocale";
import { formatDateByLocale } from "../../../lib/localeFormatters";
import {
  clampDateToMonthFilterRange,
  getMonthFilterMaxDate,
  getMonthFilterYears,
  getSelectableMonthIndices,
} from "../../../lib/reportUtils";

/**
 * MonthYearPicker Component
 * Simple picker that shows only month and year dropdowns
 */
const MonthYearPicker = ({
  value,
  onChange,
  placeholder,
  label,
  subtitle,
  id,
  className,
}) => {
  const { t } = useTranslation("reports");
  const { locale } = useUiLocale();
  const maxDate = getMonthFilterMaxDate();
  const clampedValue = value ? clampDateToMonthFilterRange(value) : null;
  const fallbackYear = clampedValue?.getFullYear() ?? maxDate.getFullYear();
  const fallbackMonth = clampedValue?.getMonth() ?? maxDate.getMonth();
  const [open, setOpen] = useState(false);
  const [tempMonth, setTempMonth] = useState(fallbackMonth);
  const [tempYear, setTempYear] = useState(fallbackYear);

  const months = Array.from({ length: 12 }, (_, index) =>
    new Intl.DateTimeFormat(locale, { month: "long" }).format(
      new Date(2020, index, 1),
    ),
  );

  const years = getMonthFilterYears();
  const availableMonths = useMemo(
    () => getSelectableMonthIndices(tempYear),
    [tempYear],
  );

  const handleConfirm = () => {
    const newDate = clampDateToMonthFilterRange(
      new Date(tempYear, tempMonth, 1),
    );
    onChange?.(newDate);
    setOpen(false);
  };

  const handleCancel = () => {
    setTempMonth(fallbackMonth);
    setTempYear(fallbackYear);
    setOpen(false);
  };

  // Sync temp values with value when popover opens
  useEffect(() => {
    if (open) {
      setTempMonth(fallbackMonth);
      setTempYear(fallbackYear);
    }
  }, [open, fallbackMonth, fallbackYear]);

  // Keep month valid when switching years with different month bounds.
  useEffect(() => {
    if (!availableMonths.length) return;
    if (!availableMonths.includes(tempMonth)) {
      setTempMonth(availableMonths[availableMonths.length - 1]);
    }
  }, [availableMonths, tempMonth]);

  const formatDisplayValue = (date) => {
    if (!date) {
      return placeholder || t("monthYearPicker.placeholder");
    }

    return formatDateByLocale(
      date,
      {
        year: "numeric",
        month: "long",
      },
      locale,
    );
  };

  return (
    <div className={className}>
      <div className="flex flex-col gap-3">
        {label && (
          <Label htmlFor={id} className="px-1 text-gray-300">
            {label}
          </Label>
        )}
        {subtitle && <span className="text-xs text-gray-400">{subtitle}</span>}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outlineLight"
              id={id}
              className="h-9 w-full justify-between border-gray-700 bg-gray-800 font-normal text-white hover:bg-gray-700 focus:ring-2 focus:ring-blue-500"
            >
              {formatDisplayValue(clampedValue)}
              <Icon name="ChevronDown" className="h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto border-gray-700 bg-gray-800 p-4"
            align="start"
          >
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="mb-1 block text-xs font-medium text-gray-400">
                    {t("monthYearPicker.month")}
                  </label>
                  <select
                    value={tempMonth}
                    onChange={(e) => setTempMonth(parseInt(e.target.value))}
                    className="custom-select w-full min-w-[140px] rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    {availableMonths.map((monthIndex) => (
                      <option
                        key={monthIndex}
                        value={monthIndex}
                        className="bg-gray-700 text-white"
                      >
                        {months[monthIndex]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="mb-1 block text-xs font-medium text-gray-400">
                    {t("monthYearPicker.year")}
                  </label>
                  <select
                    value={tempYear}
                    onChange={(e) => setTempYear(parseInt(e.target.value))}
                    className="custom-select w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    {years.map((year) => (
                      <option
                        key={year}
                        value={year}
                        className="bg-gray-700 text-white"
                      >
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 border-t border-gray-700 pt-2">
                <Button
                  variant="outlineLight"
                  size="sm"
                  onClick={handleCancel}
                  className="border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600"
                >
                  {t("monthYearPicker.cancel")}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleConfirm}
                  className="bg-blue-600 text-white hover:bg-blue-700"
                >
                  {t("monthYearPicker.select")}
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default MonthYearPicker;
